/**
 * Client for the super-user API (`/api/su`), used by Kono staff signed in to the
 * buyer app.
 *
 * Every call needs a recently re-authenticated ID token: the API answers 401
 * with `reauthRequired` once the password re-entry goes stale, which is the
 * signal to prompt again rather than an error to show. `SuperUserApiError`
 * carries that flag so callers can tell the two apart.
 */
import { firebaseAuth } from "../../firebase";

/**
 * The super-user routes sit beside the buyer-facing ones on the same function
 * (`/api/bf` and `/api/su`), so the base URL is derived from the buyer one
 * rather than configured separately. That keeps a new environment from needing
 * one more variable set correctly for the feature to work at all — and if the
 * buyer API is reachable, so is this.
 */
export const superUserApiBase = (): string => {
  const buyerBase = process.env.REACT_APP_BF_API_BASE_URL || "";
  const base = buyerBase.replace(/\/bf\/?$/, "/su");
  if (base === buyerBase) {
    // The derivation only works while the buyer API is mounted at /bf. Saying so
    // beats letting every call 404 with no explanation.
    console.error(
      `Super-user API base could not be derived from "${buyerBase}": ` +
        "expected it to end in /bf.",
    );
  }
  return base;
};

export class SuperUserApiError extends Error {
  reauthRequired: boolean;

  constructor(message: string, reauthRequired = false) {
    super(message);
    this.name = "SuperUserApiError";
    this.reauthRequired = reauthRequired;
  }
}

/**
 * `fetch` directly rather than through `sendAuthenticatedRequest`, which throws
 * on any non-2xx: a 401 here is a normal part of the flow (the re-authentication
 * lapsed) and has to be read off the response, not out of an error message.
 */
const request = async <T>(
  path: string,
  options: { method?: string; jsonBody?: unknown } = {},
): Promise<T> => {
  const token = await firebaseAuth.currentUser?.getIdToken();
  const response = await fetch(`${superUserApiBase()}${path}`, {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(options.jsonBody ? { body: JSON.stringify(options.jsonBody) } : {}),
  });
  const body = await response.json().catch(() => ({}) as any);
  if (!response.ok) {
    throw new SuperUserApiError(
      body?.error || `Request failed (${response.status})`,
      response.status === 401 && !!body?.reauthRequired,
    );
  }
  return body as T;
};

export type SuperUserSession = {
  ok: boolean;
  email: string | null;
  expiresAt: string;
};

/** Confirm the caller may act as a super user now, and until when. */
export const fetchSuperUserSession = (): Promise<SuperUserSession> =>
  request<SuperUserSession>("/session");

export type PriceMoney = {
  value?: number;
  currency?: string;
  unit?: string;
};

export type ProvenanceEvent = {
  at: string;
  kind: "vendor" | "internal" | "edit";
  label: string;
  detail?: string;
  uploadId?: string;
  outcome?: string;
  vendorConfirmation: boolean;
};

export type RecordProvenance = {
  origin: ProvenanceEvent | null;
  lastVendorConfirmation: ProvenanceEvent | null;
  events: ProvenanceEvent[];
  omittedUploads: number;
  freshness: {
    sourceCategory: string | null;
    lifeDays: number;
    ageDays: number | null;
    confirmedAt: string | null;
    confirmedBy: "freshAt" | "updatedAt" | "lastManualEditAt" | null;
    visible: boolean;
    lastVendorConfirmationAt: string | null;
    internalPassesSinceVendor: number;
    wouldHaveExpiredAt: string | null;
  };
  summary: string[];
};

/** A real price that fed an estimate: whose it was and when we ingested it. */
export type PriceComparableRef = {
  id: string;
  supplierId?: string;
  supplierName?: string;
  perLbUsd: number;
  seenAt?: string;
};

export type ProductInsight = {
  compositeId: string;
  productId: string;
  summary: string | null;
  vendor: {
    supplierId: string | null;
    name: string | null;
    sourceProfile: string | null;
    sourceMethod: string | null;
  };
  /** How/when the inventory row was obtained (scrape, upload, mailbox…). */
  dataOrigin?: {
    obtainedAt: string | null;
    method: string;
    methodCode: string | null;
    sourceUrl: string | null;
    /** When a vendor source (not an internal republish) last confirmed it. */
    vendorConfirmedAt?: string | null;
  };
  /** The record's history: first upload, republishes, edits, freshness. */
  provenance?: RecordProvenance | null;
  price: {
    current: PriceMoney | null;
    origin: string;
    manuallySet: boolean;
    vendorComparison: {
      vendorValue: number;
      vendorUnit?: string;
      differenceValue: number;
      differencePct: number;
      comparable: boolean;
    } | null;
    estimate: Record<string, any> | null;
    /** Rule-based and ML estimates side by side, as stored on the item. */
    estimates?: {
      ruleBased: {
        price: PriceMoney;
        priceEstimate: Record<string, any>;
      } | null;
      ml: { price: PriceMoney; priceEstimate: Record<string, any> } | null;
      selectedMethod: string;
      mlGated?: boolean;
      identicalOverride?: boolean;
      computedAt: string;
    } | null;
    provenance: Record<string, any> | null;
    suggestion: Record<string, any> | null;
    status: string | null;
  };
  fields: {
    summary: string | null;
    shape: string | null;
    standard: string | null;
    condition: string | null;
    origin: string | null;
    dbMaterials: string[] | null;
    dimensions: {
      [name: string]: {
        lo?: { value?: number; unit?: string };
        hi?: { value?: number; unit?: string };
      };
    } | null;
    additionalSpecifications: string | null;
    sourcingNote: string | null;
    quantity: { value?: number; unit?: string } | null;
    estimatedLeadTimeDaysMin: number | null;
    estimatedLeadTimeDaysMax: number | null;
    hiddenFromBuyerUI: boolean;
  };
  dimensionVocabulary?: {
    shapeNames: string[];
    allNames: string[];
    distanceUnits: string[];
    weightUnits: string[];
  };
  /** One piece's weight from geometry and density; null when unknown. */
  pieceWeight?: { kg: number; lb: number; densityKgM3: number } | null;
  audit: {
    id: string;
    ts: string;
    editorEmail?: string;
    note?: string;
    changes: { field: string; from: unknown; to: unknown }[];
  }[];
};

export const fetchProductInsight = (
  productId: string,
): Promise<ProductInsight> =>
  request<ProductInsight>(`/product/${encodeURIComponent(productId)}`);

export type ProductEdit = {
  price?: PriceMoney;
  quantity?: { value?: number; unit?: string };
  summary?: string;
  additionalSpecifications?: string;
  sourcingNote?: string;
  shape?: string;
  standard?: string;
  condition?: string;
  origin?: string;
  dbMaterials?: string[];
  dimensions?: {
    [name: string]: {
      lo: { value: number; unit: string };
      hi: { value: number; unit: string };
    };
  };
  estimatedLeadTimeDaysMin?: number;
  estimatedLeadTimeDaysMax?: number;
  hiddenFromBuyerUI?: boolean;
  note?: string;
};

/** An exchange-rate suggestion for Convert to USD: rate, source and date, stale when the live source was down. */
export type FxRateSuggestion = {
  from: string;
  to: string;
  rate: number;
  asOf: string;
  source: string;
  stale: boolean;
  fetchedAt: string;
};

export const fetchFxRate = (from: string, to = "USD"): Promise<FxRateSuggestion> =>
  request<FxRateSuggestion>(
    `/fx-rate?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );

export const saveProductEdit = (
  productId: string,
  edit: ProductEdit,
): Promise<{ status: string; changed: number }> =>
  request(`/product/${encodeURIComponent(productId)}/edit`, {
    method: "POST",
    jsonBody: edit,
  });
